import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  useTheme,
  Checkbox,
  FormGroup,
  IconButton,
  ListItem,
  ListItemText,
  DialogActions,
  Button,
  List,
  DialogContent,
  DialogTitle,
  Modal,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_1,
  COLOR_SECONDARY_1,
  COLOR_GREY_200,
  COLOR_PRIMARY_2_100,
} from 'src/colors';
import DailyPerformanceTable, { DailyData } from './DailyPerformanceTable';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { COLOR_GREEN } from 'src/const';

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  textAlign: 'center',
  background: `linear-gradient(135deg, #f5f7fa 0%, #fff 100%)`,
  border: `1px solid ${COLOR_GREY_200}`,
  height: '120px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  border: `1px solid ${COLOR_GREY_200}`,
  marginTop: theme.spacing(3),
}));

interface ApiResponseType {
  graphData: DailyData[];
  kpiSummary: {
    month: string;
    total_commission: number;
    total_sales: number;
    total_signups: number;
    total_visitors: number;
    comission_track_failed?: string[];
  };
  name: string;
  email: string;
}

interface MonthlyKpisProps {
  data: ApiResponseType;
}

const BAR_WIDTH = 7;

const MonthlyKpis: React.FC<MonthlyKpisProps> = ({ data }) => {
  const theme = useTheme();
  const [showChart, setShowChart] = useState(true);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [selectedKpis, setSelectedKpis] = useState({
    visitors: true,
    signups: true,
    subscriptions: true,
  });

  // Format chart data for the bar chart
  const chartData = data.graphData.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    visitors: item.visitors_count,
    signups: item.signup_count,
    subscriptions: item.subscription_count,
  }));

  // Handle KPI selection toggle
  const handleKpiToggle = (kpi: keyof typeof selectedKpis) => {
    setSelectedKpis(prev => ({
      ...prev,
      [kpi]: !prev[kpi],
    }));
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            border: `1px solid ${COLOR_GREY_200}`,
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const handleWarningClick = () => {
    setOpenWarningModal(true);
  };

  const handleCloseWarningModal = () => {
    setOpenWarningModal(false);
  };

  const ModalUntrackedCommission = (
    <Modal
      open={openWarningModal}
      onClose={handleCloseWarningModal}
      aria-labelledby="commission-track-failed-modal-title"
      aria-describedby="commission-track-failed-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: theme.palette.background.paper,
          border: 'none',
          boxShadow: theme.shadows[5],
          p: 4,
          borderRadius: '12px',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <DialogTitle id="commission-track-failed-modal-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <WarningAmberRoundedIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Commission Tracking Failed
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            The following emails had issues with commission tracking for {data.kpiSummary.month}:
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              border: `1px solid ${COLOR_GREY_200}`,
              borderRadius: '8px',
              p: 1,
            }}
          >
            {data.kpiSummary.comission_track_failed &&
              data.kpiSummary.comission_track_failed.map((email, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary={email} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            {data.kpiSummary.comission_track_failed &&
              data.kpiSummary.comission_track_failed.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No failed commission tracking entries for this month."
                    primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                  />
                </ListItem>
              )}
          </List>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Please contact support for more details regarding these failed entries.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button onClick={handleCloseWarningModal} variant="contained" disableElevation>
            Close
          </Button>
        </DialogActions>
      </Box>
    </Modal>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: COLOR_PRIMARY_2, mb: 2 }}>
          Monthly KPIs
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Performance overview for {data.kpiSummary.month}
        </Typography>
      </Box>

      {/* KPI Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={2}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, mb: 1 }}>
              {data.kpiSummary.total_visitors.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Total Visitors
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={2}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR_SECONDARY_1, mb: 1 }}>
              {data.kpiSummary.total_signups.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Total Signups
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={2}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR_PRIMARY_1, mb: 1 }}>
              {data.kpiSummary.total_sales.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Total Sales
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={2}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR_GREEN, mb: 1 }}>
              ${data.kpiSummary.total_commission.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Total Commission
            </Typography>

            {data.kpiSummary.comission_track_failed &&
              data.kpiSummary.comission_track_failed.length > 0 && (
                <IconButton
                  size="small"
                  onClick={handleWarningClick}
                  sx={{
                    color: theme.palette.warning.main,
                    position: 'absolute',
                    top: 8,
                    right: 8,
                  }}
                >
                  <ErrorOutlineIcon />
                </IconButton>
              )}
          </StatCard>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <ChartContainer elevation={2}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: COLOR_PRIMARY_2, mb: 2 }}>
              Daily Performance Breakdown (Last 60 Days)
            </Typography>

            {/* KPI Selection Checkboxes */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                Show KPIs:
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedKpis.visitors}
                      onChange={() => handleKpiToggle('visitors')}
                      size="small"
                      sx={{
                        color: COLOR_PRIMARY_1,
                        '&.Mui-checked': { color: COLOR_PRIMARY_1 },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Visitors
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedKpis.signups}
                      onChange={() => handleKpiToggle('signups')}
                      size="small"
                      sx={{
                        color: COLOR_SECONDARY_1,
                        '&.Mui-checked': { color: COLOR_SECONDARY_1 },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Signups
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedKpis.subscriptions}
                      onChange={() => handleKpiToggle('subscriptions')}
                      size="small"
                      sx={{
                        color: COLOR_PRIMARY_2,
                        '&.Mui-checked': { color: COLOR_PRIMARY_2 },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Subscriptions
                    </Typography>
                  }
                />
              </FormGroup>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={showChart}
                onChange={e => setShowChart(e.target.checked)}
                color="primary"
              />
            }
            label="Chart / Table switch"
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {showChart ? (
          <Box sx={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={COLOR_GREY_200} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke={COLOR_PRIMARY_2} />
                <YAxis tick={{ fontSize: 12 }} stroke={COLOR_PRIMARY_2} />
                <Tooltip content={<CustomTooltip />} />
                {selectedKpis.visitors && (
                  <Bar
                    dataKey="visitors"
                    fill={COLOR_PRIMARY_1}
                    name="Visitors"
                    radius={[4, 4, 0, 0]}
                    barSize={BAR_WIDTH}
                  />
                )}
                {selectedKpis.signups && (
                  <Bar
                    dataKey="signups"
                    fill={COLOR_SECONDARY_1}
                    name="Signups"
                    radius={[4, 4, 0, 0]}
                    barSize={BAR_WIDTH}
                  />
                )}
                {selectedKpis.subscriptions && (
                  <Bar
                    dataKey="subscriptions"
                    fill={COLOR_PRIMARY_2}
                    name="Subscriptions"
                    radius={[4, 4, 0, 0]}
                    barSize={BAR_WIDTH}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <DailyPerformanceTable data={(data.graphData || []).reverse()} />
          </Box>
        )}
      </ChartContainer>

      {ModalUntrackedCommission}

      {/* Summary Section */}
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, borderRadius: '12px', border: `1px solid ${COLOR_GREY_200}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLOR_PRIMARY_2 }}>
            Performance Insights - {data.kpiSummary.month}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Conversion Rate (Signups/Visitors):
              </Typography>
              <Typography variant="h6" sx={{ color: COLOR_SECONDARY_1, fontWeight: 600 }}>
                {data.kpiSummary.total_visitors > 0
                  ? (
                      (data.kpiSummary.total_signups / data.kpiSummary.total_visitors) *
                      100
                    ).toFixed(2)
                  : '0'}
                %
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Average Commission per Sale:
              </Typography>
              <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                $
                {data.kpiSummary.total_sales > 0
                  ? (data.kpiSummary.total_commission / data.kpiSummary.total_sales).toFixed(2)
                  : '0'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default MonthlyKpis;
